import { designVariables } from "@studio3t/design-variables";
import { ButtonSmallSecondary } from "@studio3t/generic-components";
import { useNavigate } from "react-router-dom";
import styled, { type DefaultTheme } from "styled-components";
import { SkeletonBar, SkeletonPill } from "@/components/Skeleton";
import type { LogEntry } from "@/types/api";
import { FULL_LOGS_HEIGHT, TabEmptyState } from "./styles";

const SEMANTIC = designVariables.ui.semantic;
const BADGE = designVariables.ui.badge;
const SINGLE_LINE = designVariables.type.singleLine;

// The preview card shows only the most recent few entries; "View all logs" opens
// the full, filterable logs page.
const MAX_RECENT_LOGS = 5;

interface LogsBodyProps {
  jobName: string;
  logs: LogEntry[];
  loading: boolean;
}

function formatLogTimestamp(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return isoString;
  }
}

type LevelVariant = "error" | "warning" | "info" | "debug" | "trace";

// Map a log level onto a minimal badge variant. Errors/fatals are red, warnings
// amber, info blue; debug/trace use muted greys (debug stronger than trace) to
// de-emphasise them below info, mirroring the full Logs page.
function levelVariant(level: string): LevelVariant {
  switch (level.toLowerCase()) {
    case "error":
    case "err":
    case "fatal":
    case "critical":
      return "error";
    case "warn":
    case "warning":
      return "warning";
    case "debug":
      return "debug";
    case "trace":
      return "trace";
    default:
      return "info";
  }
}

// Normalise abbreviated levels to their full word for the badge label
// (e.g. "warn" → "warning", "err" → "error"); other levels show as-is.
const LEVEL_LABELS: Record<string, string> = { warn: "warning", err: "error" };

function levelLabel(level: string): string {
  const l = level.toLowerCase();
  return LEVEL_LABELS[l] ?? l;
}

// Skeleton rows mirror the real log-row shape (time / level badge / message)
// in the same fixed-height card — including the View-all button's footprint —
// so the swap to data doesn't shift layout.
const SKELETON_ROWS = Array.from(
  { length: MAX_RECENT_LOGS },
  (_, i) => `skeleton-${i}`,
);

const BUTTON = designVariables.ui.button;

function LogsSkeleton() {
  return (
    <Card aria-hidden="true">
      {SKELETON_ROWS.map((key) => (
        <Row key={key}>
          <SkeletonBar $width="56px" />
          <LevelSlot>
            {/* Default pill height (20px) matches the real LevelBadge height,
                keeping each skeleton row as tall as a data row. */}
            <SkeletonPill $width="48px" />
          </LevelSlot>
          <SkeletonBar $width="60%" />
        </Row>
      ))}
      <ViewAllRow>
        <SkeletonBar $width="100px" $height={`${BUTTON.s.size}px`} />
      </ViewAllRow>
    </Card>
  );
}

export function LogsBody({ jobName, logs, loading }: LogsBodyProps) {
  const navigate = useNavigate();

  if (loading) {
    return <LogsSkeleton />;
  }
  if (logs.length === 0) {
    return <TabEmptyState>No logs available for this pipeline</TabEmptyState>;
  }

  const recent = logs.slice(0, MAX_RECENT_LOGS);

  return (
    <Card>
      {recent.map((log, idx) => (
        <Row key={`${log.timestamp}-${idx}`}>
          <Time>{formatLogTimestamp(log.timestamp)}</Time>
          <LevelSlot>
            <LevelBadge $variant={levelVariant(log.level)}>
              {levelLabel(log.level)}
            </LevelBadge>
          </LevelSlot>
          <Message>{log.message}</Message>
        </Row>
      ))}
      <ViewAllRow>
        <ButtonSmallSecondary
          text="View all logs"
          onClick={() => navigate(`/logs?job=${encodeURIComponent(jobName)}`)}
        />
      </ViewAllRow>
    </Card>
  );
}

// Figma 13806-30497: column of log rows, gap spacings, padded spacingl/spacings.
// Holds a fixed (full) height regardless of how many rows there are, so the card
// doesn't shrink with 1–4 logs; the View-all button stays pinned to the bottom.
const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SEMANTIC.spacings}px;
  padding: ${SEMANTIC.spacings}px ${SEMANTIC.spacingl}px;
  min-height: ${FULL_LOGS_HEIGHT}px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacingl}px;
  width: 100%;
  overflow: hidden;
`;

const Time = styled.span`
  flex-shrink: 0;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  line-height: 1.2;
  white-space: nowrap;
  font-variant-numeric: tabular-nums lining-nums;
  color: ${(props) => props.theme.semanticTextLight};
`;

// Fixed-width slot so the message column lines up regardless of badge label
// width (Figma reserves a 60px box for the level badge).
const LevelSlot = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 60px;
`;

const BADGE_COLORS: Record<
  LevelVariant,
  (theme: DefaultTheme) => { background: string; border: string; color: string }
> = {
  error: (t) => ({
    background: t.badgeErrorMinimalBackground,
    border: t.badgeErrorMinimalBorder,
    color: t.badgeErrorMinimalColor,
  }),
  warning: (t) => ({
    background: t.badgeWarningMinimalBackground,
    border: t.badgeWarningMinimalBorder,
    color: t.badgeWarningMinimalColor,
  }),
  info: (t) => ({
    background: t.badgeInfoMinimalBackground,
    border: t.badgeInfoMinimalBorder,
    color: t.badgeInfoMinimalColor,
  }),
  debug: (t) => ({
    background: t.badgeSecondaryMinimalBackground,
    border: t.badgeSecondaryMinimalBorder,
    color: t.badgeSecondaryMinimalColor,
  }),
  trace: (t) => ({
    background: t.badgeNeutralMinimalBackground,
    border: t.badgeNeutralMinimalBorder,
    color: t.badgeNeutralMinimalColor,
  }),
};

// Minimal badge (subtle fill + solid border) per level, matching the Figma log
// rows. The error variant uses badge/error/minimal (#f44a34 border on a 10%
// red fill, #ae1c09 text).
const LevelBadge = styled.span<{ $variant: LevelVariant }>`
  display: inline-flex;
  align-items: center;
  gap: ${BADGE.gap}px;
  height: ${BADGE.height}px;
  padding: 0 ${BADGE.paddingw}px;
  border-radius: ${BADGE.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1;
  white-space: nowrap;
  text-transform: lowercase;
  background: ${(props) => BADGE_COLORS[props.$variant](props.theme).background};
  border: 1px solid ${(props) => BADGE_COLORS[props.$variant](props.theme).border};
  color: ${(props) => BADGE_COLORS[props.$variant](props.theme).color};
`;

const Message = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  line-height: 1.4;
  word-break: break-word;
  color: ${(props) => props.theme.semanticTextBody};
`;

// Sits directly under the rows (whether there's one log or five); the card keeps
// its fixed height, so any slack is empty space below the button.
const ViewAllRow = styled.div`
  padding-top: ${SEMANTIC.spacings}px;
`;
